import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute'; // Import ProtectedRoute
import CustomStyles from './components/CustomStyles'; // Import CustomStyles
import ScriptsLoader from './components/ScriptsLoader'; // Import ScriptsLoader
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';

// Admin Pages
import NotFound from './pages/NotFound';
import Services from './pages/Services';
import Industries from './pages/Industries';
import ResourcesPage from './pages/Resources';

import TrainingHub from './pages/training/TrainingHub';
import AiBdTraining from './pages/training/AiBdTraining';
import PromptEngineering from './pages/training/ai-bd/PromptEngineering';
import LeadScoring from './pages/training/ai-bd/LeadScoring';
import Personalization from './pages/training/ai-bd/Personalization';
import CrmAutomation from './pages/training/ai-bd/CrmAutomation';
import ExperiencedBdTraining from './pages/training/ExperiencedBdTraining';
import AccountMapping from './pages/training/experienced-bd/AccountMapping';
import StakeholderManagement from './pages/training/experienced-bd/StakeholderManagement';
import NegotiationTactics from './pages/training/experienced-bd/NegotiationTactics';
import ClosingDeals from './pages/training/experienced-bd/ClosingDeals';
import AspiringBdTraining from './pages/training/AspiringBdTraining';
import SalesFunnel from './pages/training/aspiring-bd/SalesFunnel';
import ColdOutreach from './pages/training/aspiring-bd/ColdOutreach';
import ObjectionHandling from './pages/training/aspiring-bd/ObjectionHandling';
import ProfessionalComm from './pages/training/aspiring-bd/ProfessionalComm';
import Webinars from './pages/training/Webinars';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import CookiePolicy from './pages/legal/CookiePolicy';
import GdprCompliance from './pages/legal/GdprCompliance';
import About from './pages/About';
import Careers from './pages/Careers';
import Contact from './pages/Contact';
import ThankYou from './pages/ThankYou';
import Calculator from './pages/Calculator';
import Glossary from './pages/Glossary';
import GlossaryTerm from './pages/GlossaryTerm';
import Blog from './pages/Blog';
import BlogCategory from './pages/BlogCategory';
import BlogPost from './pages/BlogPost';
import AuthorPage from './pages/AuthorPage';
import Login from './pages/admin/Login';
import AdminDashboard from './pages/admin/AdminDashboard';
import SEOManager from './pages/admin/SEOManager';
import BlogManager from './pages/admin/BlogManager';
import BlogEditor from './pages/admin/BlogEditor';
import GlossaryManager from './pages/admin/GlossaryManager';
import GlossaryEditor from './pages/admin/GlossaryEditor';
import AuthorManager from './pages/admin/AuthorManager';
import AuthorEditor from './pages/admin/AuthorEditor';
import NewsManager from './pages/admin/NewsManager';
import WebinarManager from './pages/admin/WebinarManager';
import ResourceManager from './pages/admin/ResourceManager';
import SitemapManager from './pages/admin/SitemapManager';
import MenuManager from './pages/admin/MenuManager';
import DesignManager from './pages/admin/DesignManager';
import ThemeManager from './pages/admin/ThemeManager';
import AIIntelligenceHub from './pages/admin/AIIntelligenceHub';
import AISettings from './pages/admin/AISettings';

// Main App Component
const App = () => {
  return (
    <>
      <ScrollToTop />
      <CustomStyles />
      <ScriptsLoader />
      <Routes>
        {/* Admin Routes */}
        <Route path="/admin/login" element={<Login />} />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="seo" element={<SEOManager />} />
          <Route path="blog" element={<BlogManager />} />
          <Route path="blog/new" element={<BlogEditor />} />
          <Route path="blog/edit/:id" element={<BlogEditor />} />
          <Route path="glossary" element={<GlossaryManager />} />
          <Route path="glossary/new" element={<GlossaryEditor />} />
          <Route path="glossary/edit/:id" element={<GlossaryEditor />} />
          <Route path="authors/new" element={<AuthorEditor />} />
          <Route path="authors/edit/:id" element={<AuthorEditor />} />

          {/* New specific routes for previously shared modules */}
          <Route path="news" element={<NewsManager />} />
          <Route path="webinars" element={<WebinarManager />} />
          <Route path="resources" element={<ResourceManager />} />
          <Route path="authors" element={<AuthorManager />} />
          <Route path="sitemaps" element={<SitemapManager />} />
          <Route path="menus" element={<MenuManager />} />
          <Route path="design" element={<DesignManager />} />
          <Route path="themes" element={<ThemeManager />} />
          <Route path="ai-hub" element={<AIIntelligenceHub />} />
          <Route path="ai-settings" element={<AISettings />} />
        </Route>

        {/* Public Routes (Wrapped in Layout) */}
        <Route path="/*" element={
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />

              {/* Services Routes */}
              <Route path="/services" element={<Services />} />
              <Route path="/services/:serviceId" element={<Services />} />

              {/* Industries Routes */}
              <Route path="/industries" element={<Industries />} />
              <Route path="/industries/:industryId" element={<Industries />} />

              {/* Resources Routes */}
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/calculator" element={<Calculator />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/glossary/:id" element={<GlossaryTerm />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/category/:category" element={<BlogCategory />} />
              <Route path="/blog/:id" element={<BlogPost />} />
              <Route path="/author/:id" element={<AuthorPage />} />
              <Route path="/case-studies" element={<ResourcesPage />} />

              {/* Training Routes */}
              <Route path="/training" element={<TrainingHub />} />
              <Route path="/training/ai-bd" element={<AiBdTraining />} />
              <Route path="/training/ai-bd/prompt-engineering" element={<PromptEngineering />} />
              <Route path="/training/ai-bd/lead-scoring" element={<LeadScoring />} />
              <Route path="/training/ai-bd/personalization" element={<Personalization />} />
              <Route path="/training/ai-bd/crm-automation" element={<CrmAutomation />} />
              <Route path="/training/experienced-bd" element={<ExperiencedBdTraining />} />
              <Route path="/training/experienced-bd/account-mapping" element={<AccountMapping />} />
              <Route path="/training/experienced-bd/stakeholder-management" element={<StakeholderManagement />} />
              <Route path="/training/experienced-bd/negotiation-tactics" element={<NegotiationTactics />} />
              <Route path="/training/experienced-bd/closing-deals" element={<ClosingDeals />} />
              <Route path="/training/aspiring-bd" element={<AspiringBdTraining />} />
              <Route path="/training/aspiring-bd/sales-funnel" element={<SalesFunnel />} />
              <Route path="/training/aspiring-bd/cold-outreach" element={<ColdOutreach />} />
              <Route path="/training/aspiring-bd/objection-handling" element={<ObjectionHandling />} />
              <Route path="/training/aspiring-bd/professional-communication" element={<ProfessionalComm />} />
              <Route path="/training/webinars" element={<Webinars />} />

              {/* Legal Routes */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/gdpr-compliance" element={<GdprCompliance />} />

              {/* Company Routes */}
              <Route path="/about" element={<About />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/thank-you" element={<ThankYou />} />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Layout>
        } />
      </Routes>
    </>
  );
};

export default App;